import AutoSizeText from "@/components/Common/AutoSizeText";
import {useCategoryStore} from "@/store/category";
import useRegistrationStore from "@/store/registration";
import {useMemo, type FC} from "react";
import {useTranslation} from "react-i18next";

const InterestSelection: FC = () => {
  const {t} = useTranslation();
  const categories = useCategoryStore(state => state.categories);
  const maxAllowedCategories = useCategoryStore(state => state.maxAllowedCategories);
  const selectedInterests = useRegistrationStore(state => state.interests);
  const selectInterest = useRegistrationStore(state => state.selectInterest);
  const canSelectMore = useMemo(
    () => selectedInterests.length < maxAllowedCategories,
    [selectedInterests, maxAllowedCategories],
  );

  return (
    <div className="flex flex-col items-stretch gap-1">
      <span className="text-neutral-grey-dark text-sm font-medium">
        {t("selectInterestsLabel", {max_interests: maxAllowedCategories})}
      </span>
      <div className="big:grid-cols-4 grid grid-cols-2 gap-4 p-1 sm:grid-cols-3 lg:grid-cols-3 2xl:grid-cols-4">
        {categories.map(category => {
          const isSelected = selectedInterests.includes(category.id);

          return (
            <button
              disabled={!canSelectMore && !isSelected}
              onClick={() => selectInterest(category.id)}
              key={category.id}
              type="button"
              className={
                "relative aspect-square overflow-hidden rounded-md border-2 bg-transparent transition-all duration-150 enabled:hover:drop-shadow-lg disabled:cursor-not-allowed" +
                (isSelected ? " border-primary-dark-solid shadow-md" : " border-transparent")
              }>
              <div className="relative h-full w-full">
                <img
                  src={(category.image640 ?? category.image)!}
                  loading="lazy"
                  alt=""
                  className="h-full w-full object-cover"
                />
                <div
                  className={
                    "absolute inset-0 z-10 transition-all duration-150 " +
                    (!canSelectMore && !isSelected
                      ? "bg-neutral-grey-light/50 backdrop-brightness-125"
                      : "backdrop-filter-none")
                  }
                />
              </div>
              <div className="absolute bottom-3 left-3 z-20 max-w-[80%] overflow-hidden">
                <div
                  className={
                    "relative flex items-center justify-center rounded-md px-2.5 py-2 " +
                    (category.name.includes(" ") ? "w-min" : "")
                  }>
                  <div className="absolute inset-0 -z-10 overflow-hidden rounded-md bg-gray-500/10 backdrop-blur-xs" />
                  <div className="absolute inset-0 -z-10 overflow-hidden rounded-md bg-gray-500/10 backdrop-blur-xs" />
                  <div className="absolute inset-0 -z-10 overflow-hidden rounded-md border border-white bg-gray-500/10 backdrop-blur-xs" />
                  <AutoSizeText
                    className="z-20 grow-0 items-center text-left font-medium text-white"
                    fontSizeMin={8}
                    maxLines={2}
                    lineHeight={1}
                    fontSizeMax={14}>
                    {category.name}
                  </AutoSizeText>
                </div>
              </div>

              <div
                className={
                  "absolute top-4 right-4 z-10 flex h-5 w-5 items-center justify-center rounded-full border bg-white p-[1px] shadow-xs transition-all duration-150 " +
                  (isSelected ? "border-primary-dark-solid" : "border-primary-light-solid")
                }>
                <div
                  className={
                    "h-4 w-4 rounded-full transition-all duration-150 " +
                    (isSelected ? "bg-primary-dark-solid" : "bg-white")
                  }
                />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default InterestSelection;
